/**
 * UnlockManager — 의상 해금 상태 판단
 * 등급별 해금 조건을 체크하고 의상이 해금되어 있는지 반환합니다.
 */
import type { Outfit, Rarity } from '../types';
import { GameState } from './GameState';
import { EconomyEngine } from './EconomyEngine';

export interface UnlockRequirement {
  label: string;       // 잠금 표시용 한국어 텍스트
  met: boolean;        // 조건 충족 여부
}

/** 등급별 기본 요구사항 정의 */
const RARITY_REQUIREMENTS: Record<Rarity, (outfit: Outfit) => UnlockRequirement[]> = {
  common: (outfit) => {
    // common: starter는 항상 해금, shop은 코인 보유 확인
    if (outfit.unlockMethod === 'starter') return [{ label: '처음부터 해금', met: true }];
    const coins = outfit.cost?.coins ?? 0;
    const hasCoins = GameState.data.currency.coins >= coins;
    return [{ label: `💰 코인 ${coins}개`, met: hasCoins }];
  },
  rare: (outfit) => {
    const stars = GameState.data.currency.stars;
    const minigamesPlayed = Object.values(GameState.data.stats.minigameBest).filter(v => (v ?? 0) > 0).length;
    const costStars = outfit.cost?.stars;
    const costCoins = outfit.cost?.coins;

    if (costStars) {
      return [{ label: `⭐ 별 ${costStars}개 모으기`, met: stars >= costStars }];
    }
    if (costCoins) {
      const hasCoins = GameState.data.currency.coins >= costCoins;
      // rare는 별 30개 OR 미니게임 5회 중 하나 충족해도 ok
      const altMet = stars >= 30 || minigamesPlayed >= 5;
      return [
        { label: `💰 코인 ${costCoins}개`, met: hasCoins },
        { label: `또는 ⭐30개 OR 🎮5회`, met: altMet },
      ];
    }
    return [{ label: '⭐ 별 30개 또는 🎮 미니게임 5회', met: stars >= 30 || minigamesPlayed >= 5 }];
  },
  epic: (outfit) => {
    const stars = GameState.data.currency.stars;
    const rivalWins = GameState.data.stats.rivalWins ?? 0;
    const costStars = outfit.cost?.stars;
    const costGems = outfit.cost?.gems;

    if (costGems) {
      const hasGems = GameState.data.currency.gems >= costGems;
      return [{ label: `💎 보석 ${costGems}개`, met: hasGems }];
    }
    if (costStars) {
      return [{ label: `⭐ 별 ${costStars}개 모으기`, met: stars >= costStars }];
    }
    return [
      { label: `⭐ 별 100개 또는 라이벌 3승`, met: stars >= 100 || rivalWins >= 3 },
    ];
  },
  legend: (outfit) => {
    const stars = GameState.data.currency.stars;
    const mathCleared = GameState.data.mathProgress.filter(m => m.clearedAt !== undefined).length;
    const petsOwned = GameState.data.ownedPets.length;
    const costGems = outfit.cost?.gems;
    const costStars = outfit.cost?.stars;

    if (costGems) {
      const hasGems = GameState.data.currency.gems >= costGems;
      return [{ label: `💎 보석 ${costGems}개`, met: hasGems }];
    }
    if (costStars) {
      return [{ label: `⭐ 별 ${costStars}개`, met: stars >= costStars }];
    }
    return [
      { label: `⭐300개 OR 수학10단 OR 펫5마리`, met: stars >= 300 || mathCleared >= 10 || petsOwned >= 5 },
    ];
  },
  season: (_outfit) => {
    const seasonProgress = GameState.data.seasonProgress ?? 0;
    return [{ label: `🌸 시즌 진행도 ${Math.round(seasonProgress * 100)}%`, met: seasonProgress >= 0.5 }];
  },
};

export const UnlockManager = {
  /**
   * 의상이 해금되어 있는지 확인 (ownedOutfits에 있는지 + 등급 조건)
   */
  isUnlocked(outfit: Outfit): boolean {
    const owned = GameState.data.ownedOutfits.some(o => o.outfitId === outfit.id);
    if (owned) return true;
    if (outfit.unlockMethod === 'starter') return true;
    return false;
  },

  /**
   * 해금 조건 충족 여부 (구매 가능한지)
   */
  canUnlock(outfit: Outfit): boolean {
    const reqs = this.getRequirements(outfit);
    // 모든 requirement 중 하나라도 met이면 구매 가능
    return reqs.some(r => r.met);
  },

  /**
   * 해금 조건 목록 반환
   */
  getRequirements(outfit: Outfit): UnlockRequirement[] {
    const fn = RARITY_REQUIREMENTS[outfit.rarity];
    if (!fn) return [{ label: '알 수 없음', met: false }];
    return fn(outfit);
  },

  /**
   * 잠금 라벨 텍스트 (짧게)
   */
  getLockLabel(outfit: Outfit): string {
    const reqs = this.getRequirements(outfit);
    const firstUnmet = reqs.find(r => !r.met);
    if (!firstUnmet) return '🔒 구매 가능';
    return `🔒 ${firstUnmet.label}`;
  },

  /**
   * 자동 해금 시도 (조건 충족 시 ownedOutfits에 추가)
   * 구매 버튼 클릭 시 호출
   */
  tryUnlock(outfit: Outfit): { success: boolean; message: string } {
    if (this.isUnlocked(outfit)) {
      return { success: true, message: '이미 보유 중' };
    }
    if (!this.canUnlock(outfit)) {
      const label = this.getLockLabel(outfit);
      return { success: false, message: label };
    }

    // 코인/별/보석 차감 — EconomyEngine.spend 경유로 currency-changed 이벤트 자동 발생
    if (outfit.cost) {
      if (!EconomyEngine.spend(outfit.cost)) {
        const need: string[] = [];
        if (outfit.cost.coins) need.push(`💰${outfit.cost.coins}`);
        if (outfit.cost.stars) need.push(`⭐${outfit.cost.stars}`);
        if (outfit.cost.gems) need.push(`💎${outfit.cost.gems}`);
        return { success: false, message: `보유량 부족 (${need.join(' ')} 필요)` };
      }
    }

    GameState.data.ownedOutfits.push({ outfitId: outfit.id, acquiredAt: Date.now() });
    GameState.persist();
    return { success: true, message: `✨ ${outfit.name} 해금!` };
  },
};
